import { calculateDeliveryFee } from '@mahawi/eshop-common/dist/src/calculate-delivery-fee';
import { formatCurrency } from '@mahawi/eshop-common/dist/src/format-currency';
import { getFlag } from '@mahawi/eshop-common/dist/src/get-flag';
import { round } from '@mahawi/eshop-common/dist/src/round';
import {
  EEventContextAction,
  EEventType,
  EOrderAction,
  EPaymentVendorType,
  type IBasketPrice,
  type ICalculatedDeliveryFee,
  type ICountry,
  type ICountryCurrencyPrice,
  type ICustomerCurrencyType,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { Field, Form, Formik } from 'formik';
import React, {
  type BaseSyntheticEvent,
  type ReactElement,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import Input from '../../fragments/input';
import Select from '../../fragments/select';
import { setDelivery } from '../../reducers/basket/reducer';
import { type IBasketState } from '../../reducers/basket/types';
import { type IConfigState } from '../../reducers/config/types';
import { type ICountryState } from '../../reducers/country/types';
import { type ICurrencyState } from '../../reducers/currency/types';
import { eventSend } from '../../reducers/event/reducer';
import { type ILanguageState } from '../../reducers/language/types';
import { create as createOrder } from '../../reducers/order/reducer';
import { type IOrderState } from '../../reducers/order/types';
import { type IUserState } from '../../reducers/user/types';
import { type ITagManager } from '../../types';
import { orderValidation } from './order-validation';

export interface IOrderFormFields {
  billingAddress: {
    name: string;
    surname: string;
    email: string;
    phone: string;
    street: string;
    streetNumber: string;
    city: string;
    zip: string;
    countryTypeIsoCode: string;
    companyName?: string;
    companyId?: string;
    companyTaxId?: string;
  };
  deliveryAddress: {
    name: string;
    surname: string;
    email: string;
    phone: string;
    street: string;
    streetNumber: string;
    city: string;
    zip: string;
    countryTypeIsoCode: string;
  };
  deliveryAddressIsDifferent: boolean;
  heurekaCZShopCertification: boolean;
  termsAndConditions: boolean;
  packagesCount: number;
  totalDeliveryFee: number;
  discount: {
    code: string;
    value: number;
    discountType: {
      code: string;
    };
  };
  deliveryNote: string;
}

interface IOrderFormProps {
  Basket: IBasketState;
  dispatch: Dispatch;
  Country: ICountryState;
  Order: IOrderState;
  User: IUserState;
  Currency: ICurrencyState;
  Language: ILanguageState;
  TagManager?: ITagManager;
  Config: IConfigState;
  totalGoodsWeight: number;
  getItemsForTagManager: () => {
    item_name: string;
    item_id: string;
    price: number;
    quantity: number;
  }[];
  setModalOpen: (isOpen: boolean) => void;
  setCompanyHasPricesWithoutTax: (hasPricesWithoutTax: boolean) => void;
  companyHasPricesWithoutTax: boolean;
  isForCompany: boolean;
}

function OrderForm({
  Basket,
  dispatch,
  Country,
  Order,
  User,
  Currency,
  Language,
  TagManager,
  Config,
  totalGoodsWeight,
  getItemsForTagManager,
  setModalOpen,
  setCompanyHasPricesWithoutTax,
  companyHasPricesWithoutTax,
  isForCompany,
}: IOrderFormProps): ReactElement {
  const { t } = useTranslation();

  const [basketPrice, setBasketPrice] = useState<IBasketPrice | undefined>(
    undefined,
  );

  const [languageCodeLower, setLanguageCodeLower] = useState<string>(
    Language.languageType.code.toLowerCase(),
  );
  const [currency, setCurrentCurrency] = useState<
    ICustomerCurrencyType | undefined
  >(undefined);

  const [purchased, setPurchased] = useState(false);
  const [beginCheckout, setBeginCheckout] = useState(false);

  const [deliveryNote, setDeliveryNote] = useState<string>('');

  useEffect((): void => {
    if (beginCheckout && TagManager) {
      const items = getItemsForTagManager();

      TagManager.dataLayer({
        dataLayer: {
          event: 'begin_checkout',
          ecommerce: {
            items,
          },
        },
      });
    }
  }, [beginCheckout, TagManager, getItemsForTagManager]);

  useEffect((): void => {
    setLanguageCodeLower(Language.languageType.code.toLowerCase());
  }, [Language.languageType.code]);

  useEffect((): void => {
    const currencyUE: ICustomerCurrencyType | undefined =
      Currency.currencies.find(
        (c: ICustomerCurrencyType): boolean =>
          c.isoCode === User.currencyType?.isoCode,
      );

    setCurrentCurrency(currencyUE);

    const basketPriceUE: IBasketPrice | undefined = Basket.basketPrices.find(
      ({ currencyPrice: { isoCode } }: IBasketPrice): boolean =>
        isoCode === User.currencyType?.isoCode,
    );

    if (basketPriceUE) {
      setBasketPrice(basketPriceUE);
    }
  }, [
    dispatch,
    Currency.currencies,
    User.currencyType?.isoCode,
    Basket.basketPrices,
  ]);

  useEffect((): void => {
    let deliveryAddressIsoCode: string = Basket.deliveryAddressIsDifferent
      ? Basket.deliveryAddress.countryTypeIsoCode
      : Basket.billingAddress.countryTypeIsoCode;

    if (Basket.deliveryAddressIsDifferent && deliveryAddressIsoCode === '...') {
      deliveryAddressIsoCode = Basket.billingAddress.countryTypeIsoCode;
    }

    if (
      currency &&
      deliveryAddressIsoCode &&
      deliveryAddressIsoCode !== '...'
    ) {
      const delivery: ICalculatedDeliveryFee = calculateDeliveryFee(
        deliveryAddressIsoCode,
        Country.deliveries,
        currency.isoCode,
        totalGoodsWeight,
        [],
        [],
      );

      dispatch(setDelivery(delivery));
    }
  }, [
    Basket.deliveryAddressIsDifferent,
    Basket.deliveryAddress.countryTypeIsoCode,
    Basket.billingAddress.countryTypeIsoCode,
    Country.deliveries,
    currency,
    dispatch,
    totalGoodsWeight,
  ]);

  useEffect((): void => {
    if (purchased && TagManager) {
      const items = getItemsForTagManager();

      TagManager.dataLayer({
        dataLayer: {
          event: 'purchase',
          ecommerce: {
            items,
          },
          currency: 'EUR',
          transaction_id: Order.uuid,
        },
      });
    }
  }, [purchased, TagManager, Order.uuid, getItemsForTagManager]);

  const initialValuesOrder = {
    billingAddress: Basket.billingAddress,
    deliveryAddress: Basket.deliveryAddress,
    deliveryAddressIsDifferent: Basket.deliveryAddressIsDifferent,
    heurekaCZShopCertification: Basket.heurekaCZShopCertification,
    packagesCount: Basket.packagesCount,
    termsAndConditions: Basket.termsAndConditions,
    totalDeliveryFee: companyHasPricesWithoutTax
      ? Basket.delivery.fee.withoutTax
      : Basket.delivery.fee.withTax,
    discount: {
      code: Basket.discount.code,
      value: 0,
      discountType: {
        code: '',
      },
    },
    deliveryNote,
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValuesOrder}
        onSubmit={({
          billingAddress,
          deliveryAddress,
          deliveryAddressIsDifferent,
          heurekaCZShopCertification,
          deliveryNote,
        }: IOrderFormFields): void => {
          setPurchased(true);
          setModalOpen(true);

          if (!currency) {
            return;
          }

          dispatch(
            createOrder({
              billingAddress,
              deliveryAddress,
              deliveryAddressIsDifferent,
              currencyType: currency,
              languageType: Language.languageType,
              heurekaCZShopCertification,
              discount: Basket.discount,
              paymentVendorTypeCode: EPaymentVendorType.COMGATE,
              type: EOrderAction.CREATE,
              deliveryNote,
              isForCompany,
            }),
          );

          dispatch(
            eventSend({
              type: EEventType.PAGE_ORDER,
              action: EEventContextAction.SUBMIT,
            }),
          );
        }}
        validate={(values: IOrderFormFields) =>
          orderValidation({
            values,
            dispatch,
            setBeginCheckout,
            configCountryTypeIsoCode: Config.countryType.isoCode,
            setCompanyHasPricesWithoutTax,
            isForCompany,
            t,
            countries: Country.countries,
            deliveryNote,
          })
        }
      >
        {({ values, errors }) => (
          <Form>
            <div className="container--5 container--t order">
              <h3>{t('basket.billingAddress')}</h3>

              {isForCompany && (
                <>
                  <Input
                    errorMessage={errors?.billingAddress?.companyName}
                    name="billingAddress.companyName"
                    label={t('basket.companyName')}
                  />

                  <Input
                    errorMessage={errors?.billingAddress?.companyId}
                    name="billingAddress.companyId"
                    label={t('basket.companyId')}
                  />

                  <Input
                    errorMessage={errors?.billingAddress?.companyTaxId}
                    name="billingAddress.companyTaxId"
                    label={t('basket.companyTaxId')}
                  />
                </>
              )}

              <Input
                errorMessage={errors?.billingAddress?.name}
                name="billingAddress.name"
                label={t('basket.name')}
              />

              <Input
                errorMessage={errors?.billingAddress?.surname}
                name="billingAddress.surname"
                label={t('basket.surname')}
              />

              <Input
                errorMessage={errors?.billingAddress?.email}
                name="billingAddress.email"
                label={t('basket.email')}
                type="email"
              />

              <Input
                errorMessage={errors?.billingAddress?.phone}
                name="billingAddress.phone"
                label={t('basket.phone')}
                type="tel"
              />

              <Input
                errorMessage={errors?.billingAddress?.street}
                name="billingAddress.street"
                label={t('basket.street')}
              />

              <Input
                errorMessage={errors?.billingAddress?.streetNumber}
                name="billingAddress.streetNumber"
                label={t('basket.streetNumber')}
              />

              <Input
                errorMessage={errors?.billingAddress?.city}
                name="billingAddress.city"
                label={t('basket.city')}
              />

              <Input
                errorMessage={errors?.billingAddress?.zip}
                name="billingAddress.zip"
                label={t('basket.zip')}
              />

              <Select
                errorMessage={errors?.billingAddress?.countryTypeIsoCode}
                name="billingAddress.countryTypeIsoCode"
                label={t('basket.country')}
                options={Country.countries.map(
                  (c: ICountry): { value: string; label: string } => ({
                    value: c.isoCode,
                    label: `${getFlag({ countryTypeIsoCode: c.isoCode })} ${c.name} (${c.isoCode})`,
                  }),
                )}
                placeholder="..."
              />
            </div>

            {values.deliveryAddressIsDifferent && (
              <div className="container--5 container--t order">
                <h3>{t('basket.deliveryAddress')}</h3>

                <Input
                  errorMessage={errors?.deliveryAddress?.name}
                  name="deliveryAddress.name"
                  label={t('basket.name')}
                />

                <Input
                  errorMessage={errors?.deliveryAddress?.surname}
                  name="deliveryAddress.surname"
                  label={t('basket.surname')}
                />

                <Input
                  errorMessage={errors?.deliveryAddress?.email}
                  name="deliveryAddress.email"
                  label={t('basket.email')}
                />

                <Input
                  errorMessage={errors?.deliveryAddress?.phone}
                  name="deliveryAddress.phone"
                  label={t('basket.phone')}
                />

                <Input
                  errorMessage={errors?.deliveryAddress?.street}
                  name="deliveryAddress.street"
                  label={t('basket.street')}
                />

                <Input
                  errorMessage={errors?.deliveryAddress?.streetNumber}
                  name="deliveryAddress.streetNumber"
                  label={t('basket.streetNumber')}
                />

                <Input
                  errorMessage={errors?.deliveryAddress?.city}
                  name="deliveryAddress.city"
                  label={t('basket.city')}
                />

                <Input
                  errorMessage={errors?.deliveryAddress?.zip}
                  name="deliveryAddress.zip"
                  label={t('basket.zip')}
                />

                <Select
                  errorMessage={errors?.deliveryAddress?.countryTypeIsoCode}
                  name="deliveryAddress.countryTypeIsoCode"
                  label={t('basket.country')}
                  options={Country.countries.map(
                    (c: ICountry): { value: string; label: string } => ({
                      value: c.isoCode,
                      label: `${getFlag({ countryTypeIsoCode: c.isoCode })} ${c.name} (${c.isoCode})`,
                    }),
                  )}
                  placeholder="..."
                />
              </div>
            )}

            <div className="container">
              <label
                className="label-checkbox"
                htmlFor="deliveryAddressIsDifferent"
              >
                <Field
                  type="checkbox"
                  name="deliveryAddressIsDifferent"
                  id="deliveryAddressIsDifferent"
                  onClick={(e: BaseSyntheticEvent): void => {
                    dispatch(
                      eventSend({
                        type: EEventType.ORDER_DELIVERY_ADDRESS_IS_DIFFERENT,
                        action: EEventContextAction.CHANGE,
                        context: { isEnabled: !!e.target.checked },
                      }),
                    );
                  }}
                />
                {t('basket.deliveryAddressIsDifferent')}
              </label>

              <label
                className="label-checkbox"
                htmlFor="termsAndConditions"
                style={{
                  color: values.termsAndConditions ? undefined : 'red',
                }}
              >
                <Field
                  type="checkbox"
                  name="termsAndConditions"
                  id="termsAndConditions"
                  onClick={(e: BaseSyntheticEvent) => {
                    dispatch(
                      eventSend({
                        type: EEventType.ORDER_TERMS_AND_CONDITION,
                        action: EEventContextAction.CHANGE,
                        context: { isEnabled: !!e.target.checked },
                      }),
                    );
                  }}
                />
                {t('basket.acceptingTermsAndConditions')}
                &nbsp;
                <Link to={`/${languageCodeLower}/terms-and-conditions`}>
                  {t('basket.termsAndConditions')}
                </Link>
              </label>

              {errors && errors.termsAndConditions && (
                <p className="MuiFormHelperText-root Mui-error">
                  {errors.termsAndConditions}
                </p>
              )}

              {User.countryType?.isoCode === 'CZE' && (
                <label
                  className="label-checkbox"
                  htmlFor="heurekaCZShopCertification"
                >
                  <Field
                    type="checkbox"
                    name="heurekaCZShopCertification"
                    id="heurekaCZShopCertification"
                    onClick={(e: BaseSyntheticEvent): void => {
                      dispatch(
                        eventSend({
                          type: EEventType.ORDER_HEUREKA_CZ_SHOP_CERTIFICATION,
                          action: EEventContextAction.CHANGE,
                          context: { isEnabled: !!e.target.checked },
                        }),
                      );
                    }}
                  />
                  {t('basket.heurekaCZShopCertification')}
                </label>
              )}
            </div>

            <div className="container--5 order">
              <Input
                name="deliveryNote"
                label={t('basket.deliveryNote')}
                onBlur={(e: BaseSyntheticEvent): void => {
                  setDeliveryNote(e.target.value);
                }}
              />
            </div>

            {basketPrice && (
              <div className="container">
                <h2>{t('basket.summary')}</h2>

                <p className="basket--summary">
                  {t('basket.totalPriceForGoodsIs', {
                    price: formatCurrency(
                      companyHasPricesWithoutTax
                        ? basketPrice.currencyPrice.priceWithoutTax
                        : basketPrice.currencyPrice.pricesWithTax.find(
                            (p: ICountryCurrencyPrice): boolean =>
                              p.isoCode ===
                              (User.countryType?.isoCode || 'CZE'),
                          )?.price || 0,
                      currency,
                    ),
                  })}
                </p>

                <p className="basket--summary">
                  {t('basket.totalGoodsWeight', { totalGoodsWeight })}
                </p>

                <p className="basket--summary">
                  {t('basket.totalDeliveryFee', {
                    packagesCount: 1,
                    totalDeliveryFee: formatCurrency(
                      values.totalDeliveryFee,
                      currency,
                    ),
                  })}
                </p>

                {isForCompany && companyHasPricesWithoutTax && (
                  <p className="basket--summary">
                    {t('basket.allPricesIsWithoutTax')}
                  </p>
                )}

                {isForCompany && !companyHasPricesWithoutTax && (
                  <p className="basket--summary">
                    {t('basket.allPricesIsWithTax')}
                  </p>
                )}

                {Object.keys(errors).length !== 0 ||
                values.packagesCount === 0 ||
                values.termsAndConditions !== true ? (
                  <div className="alert">
                    <span>
                      {t('basket.pleaseCheckYourOrderFormForAllRequiredFields')}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="notification">
                      <span>
                        {t(
                          'basket.yourOrderIsReadyToBeSendYouCanCreateItAndPayViaButtonBelow',
                        )}
                      </span>
                    </div>

                    <button
                      className={`order--button--submit ${
                        Object.keys(errors).length !== 0 ||
                        values.packagesCount === 0 ||
                        values.termsAndConditions !== true
                          ? 'disabled'
                          : ''
                      }`}
                      type="submit"
                      disabled={
                        Object.keys(errors).length !== 0 ||
                        values.packagesCount === 0 ||
                        values.termsAndConditions !== true
                      }
                    >
                      {t('basket.createOrderAndPayViaComgatePrice', {
                        price: currency
                          ? formatCurrency(
                              companyHasPricesWithoutTax
                                ? basketPrice.currencyPrice.priceWithoutTax +
                                    values.totalDeliveryFee
                                : (basketPrice.currencyPrice.pricesWithTax.find(
                                    (p: ICountryCurrencyPrice): boolean =>
                                      p.isoCode ===
                                      (User.countryType?.isoCode || 'CZE'),
                                  )?.price || 0) + values.totalDeliveryFee,
                              currency,
                            )
                          : round(
                              companyHasPricesWithoutTax
                                ? basketPrice.currencyPrice.priceWithoutTax +
                                    values.totalDeliveryFee
                                : (basketPrice.currencyPrice.pricesWithTax.find(
                                    (p: ICountryCurrencyPrice): boolean =>
                                      p.isoCode ===
                                      (User.countryType?.isoCode || 'CZE'),
                                  )?.price || 0) + values.totalDeliveryFee,
                              2,
                            ),
                      })}
                    </button>
                  </>
                )}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}

const mapStateToProps = ({
  Basket,
  Order,
  User,
  Country,
  Currency,
  Language,
  Config,
}) => ({
  Basket,
  Order,
  User,
  Country,
  Currency,
  Language,
  Config,
});

export default connect(mapStateToProps)(OrderForm);
